import { Button, toast } from '@pella/sash-framework';
import * as classes from './SubinventoryQtyBox.module.css';
import { useState } from 'react';

/**
 * Component that displays subinventory item information in scrollable boxes.
 * Each item shows details like location, quantities, lot numbers, and transaction controls.
 * @param {Local.SubinventoryQtyBoxProps} props - Component props
 */
export const SubinventoryQtyBox = ({
  subinventoryQtyInfo,
  onAddQuantity,
  onAddFullLot,
  transactionType,
  orgId,
  disableAddQuantity = false,
  disableAddFullLot = false,
}) => {
  /** @type {[Record<number, string>, React.Dispatch<React.SetStateAction<Record<number, string>>>]} */
  const [quantities, setQuantities] = useState({});

  if (!subinventoryQtyInfo || subinventoryQtyInfo.length === 0) {
    return null;
  }

  /**
   * handle validation of adding quantity and calls onAddQuantity if valid
   * @param {Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantOutput} item
   * @param {number} index
   */
  const handleAddQuantity = (item, index) => {
    if (!quantities[index] || Number(quantities[index]) <= 0 || Number(quantities[index]) > item.unitOfIssueQoh) {
      toast.error('The entered quantity is greater than the available quantity. Please adjust the quantity');
      return;
    }
    onAddQuantity(item, Number(quantities[index]), index, transactionType, orgId);
    setQuantities((q) => ({ ...q, [index]: '' }));
  };

  /**
   * handle validation of adding full lot quantity and calls onAddFullLot if valid
   * @param {Pella.ProductionRequestAPI.GetSubinventoryInfoByPlantOutput} item
   * @param {number} qty
   * @param {number} index
   */
  const handleAddFullLot = (item, qty, index) => {
    if (!qty || qty > item.unitOfIssueQoh) {
      toast.error('The entered quantity is greater than the available quantity. Please adjust the quantity');
      return;
    }
    onAddFullLot(item, qty, index, transactionType, orgId);
    setQuantities((q) => ({ ...q, [index]: '' }));
  };

  return (
    <div className={classes.container}>
      {subinventoryQtyInfo.map((item, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key -- API can return multiple rows with identical item/locator/lot triples and no per-row id
          key={`${item.inventoryItemId}-${item.subinventoryCode}-${item.locator}-${item.lotNumber}-${index}`}
          className={classes.itemBox}
          data-testid={`subinventory-item-${index}`}
        >
          <div className={classes.itemGrid}>
            {/* First row */}
            <div className={classes.gridItem}>
              <span className={classes.label}>Item</span>
              <span className={classes.value} data-testid={`item-${index}`}>
                {item.item || 'N/A'}
              </span>
            </div>
            <div className={classes.gridItem}>
              <span className={classes.label}>Location</span>
              <span className={classes.value} data-testid={`location-${index}`}>
                {item.locator || 'N/A'}
              </span>
            </div>
            <div className={classes.gridItem}>
              <span className={classes.label}>Available QTY</span>
              <span className={classes.value} data-testid={`available-qty-${index}`}>
                {item.unitOfIssueQoh || 0}
              </span>
            </div>

            {/* Second row */}
            <div className={classes.gridItem}>
              <span className={classes.label}>UOM</span>
              <span className={classes.value} data-testid={`uom-${index}`}>
                {item.unitOfIssueCode || 'N/A'}
              </span>
            </div>
            <div className={classes.gridItem}>
              <span className={classes.label}>Lot</span>
              <span className={classes.value} data-testid={`lot-${index}`}>
                {item.lotNumber || 'N/A'}
              </span>
            </div>
            <div className={classes.gridItem}>
              <span className={classes.label}>Lot Qty</span>
              <span className={classes.value} data-testid={`lot-qty-${index}`}>
                {item.unitOfIssueFixedLot || 0}
              </span>
            </div>
          </div>

          {/* Transaction controls */}
          <div className={classes.transactionControls}>
            <div className={classes.inputGroup}>
              <label htmlFor={`transact-qty-${index}`} className={classes.label}>
                Transact QTY
              </label>
              <input
                type='number'
                id={`transact-qty-${index}`}
                className={classes.transactInput}
                placeholder=''
                data-testid={`transact-qty-${index}`}
                value={quantities[index] ?? ''}
                onChange={(e) => setQuantities((q) => ({ ...q, [index]: e.target.value }))}
              />
            </div>
            <div className={classes.buttonGroup}>
              <Button
                color='primary'
                className={classes.actionButton}
                value={quantities[index] ?? ''}
                disabled={disableAddQuantity || !quantities[index] || Number(quantities[index]) <= 0}
                onClick={() => handleAddQuantity(item, index)}
                data-testid={`add-quantity-btn-${index}`}
              >
                Add Quantity
              </Button>
              <Button
                color='secondary'
                className={classes.actionButton}
                disabled={disableAddFullLot || !item.unitOfIssueFixedLot}
                onClick={() => handleAddFullLot(item, item.unitOfIssueFixedLot, index)}
                data-testid={`add-full-lot-btn-${index}`}
              >
                Add Full Lot
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
